// SL.L1a-2 (#2957, rulings 5, 7 and 11; recon Q3) — close a sprint whose gate
// fails: one disposition per open item, a per-item reason for a cancel, and an
// overall reason. The open items, their preselected defaults, each `why` and
// whether carry is possible all come from GET …/close-gate; the server checks
// every disposition again (G1).
//
// The trimmed 20-character minimum is enforced here too (ruling 7), because a
// Zod 400 says only "Validation failed" in `error`. After a 400 or 409 the gate
// and the sprints are re-read, keeping the choices for items still open
// (ruling 5); a gate that now passes closes the dialog, and the board then
// offers plain Close.
import { useCallback, useEffect, useId, useRef, useState, type CSSProperties } from 'react';
import type { SprintItemDisposition } from '@blueprint/shared';
import SlideOver from '../SlideOver';
import { ApiError } from '../../lib/api';
import type { Sprint } from '../../lib/sprintsApi';
import {
  closeSprintWithOverride, fetchCloseGate, refusalText,
  type CloseGateCarry, type CloseGateOpenItem, type CloseGatePreview,
} from '../../lib/sprintActionsApi';
import { DialogFooter, dialogButton, primaryButton } from './SprintConfirmDialog';

// routes/sprints.ts overrideBody: the reason 20–4000 once trimmed; a disposition's reason ≤ 2000.
const MIN_REASON = 20;
const MAX_REASON = 4000;
const MAX_ITEM_REASON = 2000;

type Choice = { disposition: SprintItemDisposition; reason: string };

/**
 * One choice per open item: kept while the item is still open, else the
 * preview's default. A kept carry_over the re-read can no longer carry reverts
 * to the default, so the refused choice isn't resent (fix round R1-1).
 */
function mergeChoices(prev: Readonly<Record<string, Choice>>, preview: CloseGatePreview): Record<string, Choice> {
  return Object.fromEntries(preview.openItems.map((o) => {
    const kept = prev[o.itemId];
    const refused = !preview.carry.available && kept?.disposition === 'carry_over';
    return [o.itemId, kept && !refused ? kept : { disposition: o.defaultDisposition, reason: '' }];
  }));
}

const text: CSSProperties = { fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', margin: '4px 0 0' };
const muted: CSSProperties = { ...text, color: 'var(--text-secondary)' };
const label: CSSProperties = { display: 'block', fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600, color: 'var(--text)', margin: '8px 0 2px' };
const field: CSSProperties = {
  width: '100%', boxSizing: 'border-box', fontFamily: 'var(--font-body)', fontSize: 13, padding: '6px 8px',
  border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', background: 'var(--surface)', color: 'var(--text)',
};
const alert: CSSProperties = { ...text, color: 'var(--error)', margin: '0 0 var(--space-4)' };

function OpenItemRow({ item, title, choice, carry, onChange }: {
  item: CloseGateOpenItem; title: string | undefined; choice: Choice; carry: CloseGateCarry; onChange: (c: Choice) => void;
}) {
  const id = useId();
  const [selectId, whyId, carryId, reasonId] = [`${id}-d`, `${id}-why`, `${id}-carry`, `${id}-reason`];
  return (
    <fieldset style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '8px 12px', margin: '0 0 var(--space-3)' }}>
      <legend style={{ ...text, fontWeight: 600, padding: '0 4px' }}>{title ? `${item.key}: ${title}` : item.key}</legend>
      <label htmlFor={selectId} style={label}>{`Disposition for ${item.key}`}</label>
      <select
        id={selectId}
        value={choice.disposition}
        aria-describedby={carry.available ? whyId : `${whyId} ${carryId}`}
        onChange={(e) => onChange({ ...choice, disposition: e.target.value as SprintItemDisposition })}
        style={{ ...field, cursor: 'pointer' }}
      >
        <option value="return_to_backlog">Return to the backlog</option>
        <option value="carry_over" disabled={!carry.available}>
          {carry.available ? `Carry to Sprint ${carry.number}` : 'Carry to the next sprint'}
        </option>
        <option value="cancel">Cancel the item</option>
      </select>
      {!carry.available && <p id={carryId} style={muted}>{carry.refusal}</p>}
      <p id={whyId} style={muted}>{item.why}</p>
      {choice.disposition === 'cancel' && (
        <>
          <label htmlFor={reasonId} style={label}>{`Reason for cancelling ${item.key}`}</label>
          <textarea
            id={reasonId} rows={2} maxLength={MAX_ITEM_REASON} value={choice.reason}
            onChange={(e) => onChange({ ...choice, reason: e.target.value })} style={field}
          />
        </>
      )}
    </fieldset>
  );
}

export default function CloseOverrideDialog({ projectId, sprint, onClosed, onRefused, onDismiss, onBusy }: {
  projectId: string;
  sprint: Sprint;
  /** The sprint closed. */
  onClosed: () => void;
  /** After a 400 or 409: re-read GET /sprints. */
  onRefused: () => Promise<void>;
  /** Closed without closing the sprint; `notice` is a refusal for the board to show. */
  onDismiss: (notice?: string) => void;
  onBusy: (busy: boolean) => void;
}) {
  const [preview, setPreview] = useState<CloseGatePreview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [choices, setChoices] = useState<Record<string, Choice>>({});
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reasonId = useId();
  const [counterId, hintId] = [`${reasonId}-count`, `${reasonId}-hint`];

  // The latest onDismiss, for a read that resolves after a re-render.
  const dismissRef = useRef(onDismiss);
  useEffect(() => { dismissRef.current = onDismiss; });

  const load = useCallback(async (refusal?: string) => {
    try {
      const p = await fetchCloseGate(projectId, sprint.id);
      // Every item is now done or cancelled: plain Close is the action (ruling 5).
      if (p.result === 'pass') { dismissRef.current(refusal); return; }
      setPreview(p);
      setChoices((prev) => mergeChoices(prev, p));
      setLoadError(null);
    } catch (e) {
      setLoadError(refusalText(e, "Couldn't read the close gate."));
    }
  }, [projectId, sprint.id]);

  useEffect(() => { void load(); }, [load]);

  const titles = new Map(sprint.items.map((i) => [i.id, i.statement]));
  const trimmed = reason.trim().length;
  const short = trimmed < MIN_REASON;

  async function submit(p: CloseGatePreview) {
    setSubmitting(true);
    onBusy(true);
    setError(null);
    try {
      await closeSprintWithOverride(projectId, sprint.id, {
        reason: reason.trim(),
        dispositions: p.openItems.map((o) => {
          const c = choices[o.itemId]!; // mergeChoices holds one per open item
          return c.disposition === 'cancel'
            ? { itemId: o.itemId, disposition: c.disposition, reason: c.reason }
            : { itemId: o.itemId, disposition: c.disposition };
        }),
      });
      onClosed();
    } catch (e) {
      const refusal = refusalText(e, "The sprint couldn't be closed.");
      setError(refusal);
      if (e instanceof ApiError && (e.status === 400 || e.status === 409)) await Promise.all([load(refusal), onRefused()]);
    } finally {
      setSubmitting(false);
      onBusy(false);
    }
  }

  return (
    <SlideOver
      isOpen
      onClose={() => onDismiss()}
      title={`Close ${sprint.name} with override`}
      subtitle="Some items aren't finished. Choose what happens to each, and say why the sprint closes now."
      width="lg"
      footer={(
        <DialogFooter>
          <button type="button" onClick={() => onDismiss()} style={dialogButton}>Cancel</button>
          <button
            // Disabled until the preview has loaded, so `preview` is set when clicked.
            type="button" onClick={() => void submit(preview!)} disabled={!preview || submitting || short}
            style={{ ...primaryButton, opacity: !preview || submitting || short ? 0.6 : 1 }}
          >
            Close sprint
          </button>
        </DialogFooter>
      )}
    >
      {error && <p role="alert" style={alert}>{error}</p>}
      {!preview ? (
        loadError ? <p role="alert" style={alert}>{loadError}</p> : <p style={muted}>Loading the close gate…</p>
      ) : (
        <>
          {preview.openItems.map((o) => (
            <OpenItemRow
              key={o.itemId} item={o} title={titles.get(o.itemId)} carry={preview.carry}
              choice={choices[o.itemId]!}
              onChange={(c) => setChoices((prev) => ({ ...prev, [o.itemId]: c }))}
            />
          ))}
          <label htmlFor={reasonId} style={label}>Reason for closing with open items</label>
          <textarea
            id={reasonId} rows={4} maxLength={MAX_REASON} value={reason}
            aria-describedby={short ? `${counterId} ${hintId}` : counterId}
            onChange={(e) => setReason(e.target.value)} style={field}
          />
          <p id={counterId} aria-live="polite" style={muted}>{`${trimmed} of ${MAX_REASON} characters`}</p>
          {short && (
            <p id={hintId} style={muted}>At least 20 characters are needed, not counting spaces at the start or end.</p>
          )}
        </>
      )}
    </SlideOver>
  );
}
